import cn from "classnames";

import { SnwButtonType } from "@@/components/Button/buttonProps";
import SnwButtonContainer from "@@/components/Button/SnwButtonContainer";
import RichText from "@@/components/RichText";
import { BackgroundColor } from "@@/containers/Cards/description";
import Box from "@@/elements/Box";
import Picture from "@@/elements/Picture";
import Typhography from "@@/elements/Typography";
import useBreakpoint, {
  ScreenSize,
  useIsBreakpointOrBelow,
} from "@@/hooks/device/use-breakpoint";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { PictureData } from "@@/models/pictureData";
import styles from "./index.module.scss";

export type DescriptionCardProperties = {
  header: string;
  image: PictureData;
  body: string;
};

export type DescriptionOwnProps = {
  size: "small" | "large";
  buttons?: SnwButtonType[];
  backgroundColor: BackgroundColor;
  slider: boolean;
  anchorId?: string;
};

export type DescriptionCardProps = DescriptionCardProperties &
DescriptionOwnProps;

export const colorMapperCard = (color) => {
  switch (color) {
    case "beige":
      return "White";
    case "white":
      return "ComplementaryBeigeTint03";
    default:
      return "White";
  }
};

const DescriptionCard = ({
  backgroundColor,
  header,
  image,
  body,
  size,
  buttons,
  slider,
  anchorId,
}: DescriptionCardProps) => {
  const isScreenSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);
  const breakpoint = useBreakpoint();
  const isScreenMedium = breakpoint === ScreenSize.MEDIUM;
  
  const small = size === "small" || isScreenSmall;
  const medium = size === "small" && isScreenMedium && !slider;

  const setAspectRatio = () => {
    if (small && (!isScreenMedium || slider)) {
      return "5 / 3";
    }
    if (small && isScreenMedium && !slider) {
      return "1 /1";
    }
    return "21 /9";
  };

  const cardClassNames = cn(styles.card, {
    [styles.card_large]: !small,
    [styles.card_medium]: medium,
    [styles.card_small]: small && !medium,
  });

  return (
    <Box
      id={anchorId}
      height={slider ? "full" : null}
      width="full"
      backgroundColor={colorMapperCard(backgroundColor)}
      className={cardClassNames}
    >
      <EpiProperty<DescriptionCardProperties> name="image">
        <Picture
          className={styles.image}
          imageData={image}
          aspectRatio={setAspectRatio()}
          sizes={[216, 360, 560, 720, 900, 1200, 1600]}
        />
      </EpiProperty>
      <Box className={styles.text_container} pb={10}>
        <Box>
          <EpiProperty<DescriptionCardProperties> name="header">
            <Typhography
              pt={10}
              px={small ? 7 : 15}
              mb={4}
              textAlign="left"
              asElement="h3"
              variant="heading5"
            >
              {header}
            </Typhography>
          </EpiProperty>
          <Box px={small ? 7 : 15}>
            <EpiProperty<DescriptionCardProperties> name="body">
              <RichText body={body} />
            </EpiProperty>
          </Box>
        </Box>
        {buttons && (
          <Box
            className={buttons.length > 1 ? styles.button_wrapper : null}
            px={small ? 7 : 15}
            mt={6}
            gap={3}
            width="full"
            flexWrap="wrap"
            justifyContent={
              buttons && buttons.length === 1 ? "flex-end" : "space-between"
            }
            alignItems="flex-end"
            flexDirection={small ? "column-reverse" : "row"}
          >
            <SnwButtonContainer buttons={buttons} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { DescriptionCard };

export type ProvidedDescriptionCardProps =
  OptimizelyBlockProps<DescriptionCardProperties> & DescriptionCardProps;

const ProvidedDescriptionCard = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedDescriptionCardProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <DescriptionCard
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedDescriptionCard;
